import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { Politica, Recurso, Servicio, Zona } from '../../../api/ops/pagos';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Deshacer } from '../../../componentes/Deshacer';
import { Desplegable } from '../../../componentes/Desplegable';
import { EstadoCargando, EstadoError, EstadoNoContratado, EstadoSinPermiso } from '../../../componentes/EstadoPagina';
import { Icono } from '../../../componentes/Icono';
import type { NombreIcono } from '../../../componentes/iconos';
import { rolesDe } from '../../../logica/acceso';
import { useEnvioRetrasado } from '../../../logica/envioRetrasado';
import { avisoBreveDeError, estadoDeError } from '../../../logica/estados';
import { useCabecera } from '../../../marco/cabecera';
import { usePermisos } from '../../../permisos/permisos';
import type { PropsPantalla } from '../../tipos';
import { cargarSitios, mayuscula, plural, useNegocioPagos, type Sitios } from '../negocio';
import { AYUDA, PERMISOS, textoDeError } from '../textos';

/**
 * Dónde se aplican (4.3): la política de cada sitio con la operación de su módulo dueño (G36, G281): zonas y recursos
 * de Reservas (`updateZone` con `policy_id`, `updateResource` con `policy_id_override`; las estancias son zonas `stay`)
 * y servicios de Citas (`updateService` con `policy_id`). El cambio se ve al momento y sale a los 5 s salvo «Deshacer»
 * (G159: no mueve dinero). Sin cifras de la lista (G121, G140): «Hay sitios sin política». Un módulo que todavía no
 * existe da su grupo vacío.
 */
type Clave = `z:${string}` | `r:${string}` | `s:${string}`;

const SIN = '';

export function Asignacion(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, membresia, nombreNegocio, navegar } = props;
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const puede = permisos.puede(PERMISOS.politicas);
    const editable = puede && !soloLectura;
    const n = useNegocioPagos(ops, negocio);
    const envio = useEnvioRetrasado();

    const [datos, setDatos] = useState<{ politicas: Politica[]; sitios: Sitios } | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    // Lo elegido y aún sin enviar (o enviado y sin recargar): se ve al momento.
    const [elegido, setElegido] = useState<Readonly<Record<string, string>>>({});

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        if (!puede) {
            return;
        }
        let vivo = true;
        Promise.all([ops.listPaymentPolicies(negocio, { limit: 100 }), cargarSitios(ops, negocio)])
            .then(([p, sitios]) => {
                if (vivo) {
                    setDatos({ politicas: p.data.filter((x) => x.status !== 'archived'), sitios });
                    setElegido({});
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, puede, intento]);

    useCabecera({ ayuda: puede ? AYUDA.asignar : [], acciones: [] });

    if (!puede) {
        return <EstadoSinPermiso seccion="Dónde se aplican" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }
    if (error !== null && datos === null) {
        const e = estadoDeError(error);
        if (e.tipo === 'no-contratado') {
            return <EstadoNoContratado bloque="Pagos y anti no-show" incluye={['Políticas de cobro con señal y tarjeta de garantía', 'Dónde se aplica cada política']} propietario={membresia?.is_owner === true} onAnadir={null} onInicio={() => { navegar('/'); }} movil={movil} />;
        }
        return <EstadoError seccion="dónde se aplican" codigoSoporte={e.codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    if (datos === null) {
        return <EstadoCargando seccion="dónde se aplican" />;
    }

    const { politicas, sitios } = datos;
    const nombre = (id: string | null | undefined) => politicas.find((p) => p.id === id)?.name ?? '';
    const opciones = politicas.map((p) => ({ valor: p.id, texto: p.name }));
    const valor = (clave: Clave, actual: string | null | undefined) => elegido[clave] ?? actual ?? SIN;

    const cambiar = (clave: Clave, nuevo: string, texto: string, enviar: (id: string | null) => Promise<unknown>) => {
        setElegido((e) => ({ ...e, [clave]: nuevo }));
        envio.programar({
            texto,
            donde: clave,
            alDeshacer: () => {
                setElegido((e) => Object.fromEntries(Object.entries(e).filter(([k]) => k !== clave)));
            },
            enviar: () => {
                enviar(nuevo === SIN ? null : nuevo)
                    .then(recargar)
                    .catch((e: unknown) => {
                        avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? textoDeError(e, 'No he podido cambiar la política. Prueba de nuevo.') });
                        recargar();
                    });
            },
        });
    };

    const nombreZona = (z: Zona) => z.name ?? z.public_name ?? 'Zona';
    const zonas = sitios.zonas.filter((z) => z.modality !== 'stay');
    const estancias = sitios.zonas.filter((z) => z.modality === 'stay');
    const recursosDe = (z: Zona) => sitios.recursos.filter((r) => r.current_terms.zone_id === z.id);
    const hayAlgo = sitios.zonas.length + sitios.servicios.length > 0;
    const sinPolitica = sitios.zonas.some((z) => valor(`z:${z.id}`, z.policy_id) === SIN) || sitios.servicios.some((s) => valor(`s:${s.id}`, s.policy_id) === SIN);

    const fila = (clave: Clave, titulo: string, linea: string, actual: string | null | undefined, opcionNula: string, enviar: (id: string | null) => Promise<unknown>, sangria = false) => (
        <div key={clave} className={`rv-pg-sitio${sangria ? ' rv-pg-sitio--recurso' : ''}${movil ? ' rv-pg-sitio--movil' : ''}`} role="listitem">
            {sangria && <Icono nombre="corner-down-right" className="rv-pg-sitio__flecha" />}
            <span className="rv-ca-fila__texto"><strong>{titulo}</strong><span>{linea}</span></span>
            <span className="rv-pg-sitio__elegir">
                <Desplegable
                    etiqueta="Política"
                    valor={valor(clave, actual)}
                    opciones={[{ valor: SIN, texto: opcionNula }, ...opciones]}
                    bloqueado={!editable}
                    onChange={(v) => {
                        const t = v === SIN ? opcionNula : nombre(v);
                        cambiar(clave, v, `${titulo}: ${t}.`, enviar);
                    }}
                />
                {envio.pendiente?.donde === clave && <Deshacer onDeshacer={envio.deshacer} clave={envio.pendiente.clave} duracion={envio.duracion} movil={movil} />}
            </span>
        </div>
    );

    const grupo = (icono: NombreIcono, titulo: string, texto: string, filas: ReactNode[]) => (
        <section className="rv-ca-lista" aria-label={titulo}>
            <div className="rv-pg-grupo">
                <span className="rv-pg-baldosa rv-pg-baldosa--negra"><Icono nombre={icono} /></span>
                <span className="rv-ca-fila__texto"><strong>{titulo}</strong><span>{texto}</span></span>
            </div>
            <div role="list">{filas}</div>
        </section>
    );

    return (
        <div className="rv-ca-apartado">
            <p className="rv-ca-nota rv-ca-nota--gris">{`${sinPolitica ? 'Hay sitios sin política. ' : ''}Cada cambio vale para las ${plural(n.compromiso)} nuevas.`}</p>
            {!editable && <p className="rv-ca-nota"><Icono nombre="eye" /><span>El negocio está en solo lectura: puedes ver dónde se aplican, pero no cambiarlo.</span></p>}
            {!hayAlgo && (
                <div className="rv-ca-vacia">
                    <span className="rv-ca-vacia__baldosa"><Icono nombre="list-checks" /></span>
                    <strong>Todavía no hay sitios a los que asignar una política</strong>
                    <span>Las zonas, los recursos y los servicios se crean en Reservas y en Citas. Cuando los tengas, aquí eliges la política de cada uno.</span>
                </div>
            )}
            {zonas.length > 0 &&
                grupo(
                    'armchair',
                    `Zonas y ${plural(n.recurso)}`,
                    `Reservas. Un${n.recurso.endsWith('a') ? 'a' : ''} ${n.recurso} sin política usa la de su zona; si alguno tiene una propia, en una reserva de zona manda la más estricta.`,
                    zonas.flatMap((z) => [
                        fila(`z:${z.id}`, nombreZona(z), [`Zona`, `${String(z.resource_count)} ${z.resource_count === 1 ? n.recurso : plural(n.recurso)}`].join(' · '), z.policy_id, 'Sin política: no se cobra', (id) => ops.updateZonePolicy(negocio, z, id)),
                        ...recursosDe(z).map((r: Recurso) => {
                            const propia = valor(`r:${r.id}`, r.policy_id_override);
                            const deLaZona = nombre(valor(`z:${z.id}`, z.policy_id));
                            return fila(
                                `r:${r.id}`,
                                r.public_name ?? r.code ?? mayuscula(n.recurso),
                                `${mayuscula(n.recurso)} de ${nombreZona(z)}${propia === SIN ? (deLaZona === '' ? ' · sin política' : ` · usa «${deLaZona}»`) : ''}`,
                                r.policy_id_override,
                                'La de la zona',
                                (id) => ops.updateResourcePolicy(negocio, r, id),
                                true,
                            );
                        }),
                    ]),
                )}
            {sitios.servicios.length > 0 &&
                grupo(
                    'calendar',
                    'Servicios',
                    // G433: la ayuda del grupo dice también que, con plazos de cancelación gratuita distintos, vale el más largo.
                    'Citas. En una cita de varios servicios manda lo que diga la política: una señal o la suma. Si los servicios tienen plazos de cancelación gratuita distintos, vale el más largo.',
                    sitios.servicios.map((s: Servicio) => fila(`s:${s.id}`, s.current_version.name, 'Servicio', s.policy_id, 'Sin política: no se cobra', (id) => ops.updateServicePolicy(negocio, s, id))),
                )}
            {estancias.length > 0 &&
                grupo(
                    'bed-double',
                    'Estancias',
                    'Reservas de varias noches.',
                    estancias.map((z) => fila(`z:${z.id}`, nombreZona(z), 'Estancia', z.policy_id, 'Sin política: no se cobra', (id) => ops.updateZonePolicy(negocio, z, id))),
                )}
        </div>
    );
}
